import { useState } from "react";

import { ITopUnlistedCategoryResponse, IUnlistedStatsResponse, TopUnlistedVehicleResponse } from "../../../../models/Part";
import PriceFormat from "../../../../utils/priceFormat";
import DashboardTile from "../../../dashboard/DashboardTile";
import DashboardStatTableTabs from "../../../shared/Tabs/DashboardStatTableTabs";

const tabs = [
  { name: "Top Categories", value: "Categories" },
  { name: "Top Vehicles", value: "Vehicles" },
];

type TabValue = (typeof tabs)[number]["value"];

export default function UnlistedStatsTable({
  unlistedStats,
}: {
  unlistedStats: IUnlistedStatsResponse;
}) {
  const [section, setSection] = useState<TabValue>("Categories");

  const renderCategoryTable = (data: ITopUnlistedCategoryResponse[]) => (
    <table className="min-w-full border-collapse">
      <thead>
        <tr className="border-b border-gray-300 text-left text-sm font-semibold text-gray-700">
          <th className="py-2 px-3 w-1/3">Category</th>
          <th className="py-2 px-3 text-right">Located</th>
          <th className="py-2 px-3 text-right">Total</th>
          <th className="py-2 px-3 text-right">Value</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {data.length > 0 ? (
          data.map((row) => (
            <tr key={row.categoryName}>
              <td className="py-2 px-3 font-medium text-gray-900">
                {row.categoryName}
              </td>
              <td className="py-2 px-3 text-right font-semibold text-primary-600">
                {row.located}
              </td>
              <td className="py-2 px-3 text-right font-semibold text-primary-600">
                {row.count}
              </td>
              <td className="py-2 px-3 text-right font-semibold text-primary-600">
                {PriceFormat(row.value)}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={4} className="py-4 text-center text-gray-500 italic">
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderVehicleTable = (data: TopUnlistedVehicleResponse[]) => (
    <table className="min-w-full border-collapse">
      <thead>
        <tr className="border-b border-gray-300 text-left text-sm font-semibold text-gray-700">
          <th className="py-2 px-3 w-1/2">Vehicle</th>
          <th className="py-2 px-3 text-right">Total</th>
          <th className="py-2 px-3 text-right">Value</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {data.length > 0 ? (
          data.map((row) => (
            <tr key={`${row.vrm}-${row.make}-${row.model}`}>
              <td className="py-2 px-3 font-medium text-gray-900">
                {row.make} {row.model} {row.year ?? "-"} ({row.vrm})
              </td>
              <td className="py-2 px-3 text-right font-semibold text-primary-600">
                {row.count}
              </td>
              <td className="py-2 px-3 text-right font-semibold text-primary-600">
                {PriceFormat(row.value)}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={4} className="py-4 text-center text-gray-500 italic">
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <DashboardTile span={2}>
      <div className="">
        <div className="mb-4 flex items-center justify-between">
          <div className="prose">
            <h3 className="m-0">Top {section}</h3>
          </div>
          <DashboardStatTableTabs
            tabs={tabs}
            value={section}
            onChange={setSection}
            ariaLabel="Select a tab"
          />
        </div>

        <div className="overflow-x-auto overflow-y-scroll max-h-[25vh]">
          {section === "Categories"
            ? renderCategoryTable(unlistedStats.topUnlistedCategories)
            : renderVehicleTable(unlistedStats.topUnlistedVehicles)}
        </div>
      </div>
    </DashboardTile>
  );
}
